import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AdminShell } from '@/apps/coach/components/admin/AdminShell';
import { routes } from '@/lib/routes';
import { PageHeader } from '@/components/layout/PageHeader';
import { api } from '@/lib/api';

type User = Awaited<ReturnType<typeof api.operator.users.list>>[number];

export function UserList() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.operator.users.list()
      .then(setUsers)
      .catch((err) => {
        const msg = err instanceof Error ? err.message : '';
        if (msg.startsWith('API 401')) {
          navigate(routes.coach.welcome(), { replace: true });
          return;
        }
        setError(msg || 'Fehler beim Laden.');
      });
  }, [navigate]);

  return (
    <AdminShell pageTitle="Nutzer">
      <PageHeader title="Nutzer" showBack />
      <div className="flex-1 overflow-y-auto px-4 py-6 space-y-3">
        {error && <p className="text-sm text-destructive">{error}</p>}
        {users && users.length === 0 && (
          <p className="text-sm text-muted-foreground">Noch keine registrierten Nutzer.</p>
        )}
        {users?.map((u) => (
          <Link
            key={u.id}
            to={routes.coach.operator.user(u.id)}
            className="block rounded-xl border border-border px-4 py-3 space-y-1 hover:bg-muted transition-colors"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{u.name || u.email}</p>
                <p className="text-xs text-muted-foreground truncate">{u.email}</p>
              </div>
              <div className="flex flex-wrap gap-1 shrink-0">
                {u.isOperator && (
                  <span className="text-[10px] uppercase tracking-wide bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                    Operator
                  </span>
                )}
                {u.coach && (
                  <span className="text-[10px] uppercase tracking-wide bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 px-2 py-0.5 rounded-full">
                    Coach
                  </span>
                )}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Registriert am {new Date(u.createdAt).toLocaleDateString()}
            </p>
            {u.coach && (
              <p className="text-xs text-muted-foreground">
                Coach-Tenant: <span className="font-medium">{u.coach.tenantName}</span>
                {' · '}
                {u.coach.clientCount} Klient{u.coach.clientCount === 1 ? '' : 'en'}
              </p>
            )}
            {u.voucher ? (
              <p className="text-xs text-muted-foreground">
                Gutschein: <span className="font-mono">{u.voucher.code}</span>
                {' · '}
                {u.voucher.usedCount} / {u.voucher.usageLimit} Nachrichten
                {u.voucher.revoked && ' (widerrufen)'}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground italic">Kein aktiver Gutschein</p>
            )}
            {u.historyCount > 0 && (
              <p className="text-xs text-muted-foreground">
                {u.historyCount} weitere{u.historyCount === 1 ? 'r' : ''} Gutschein in der Vergangenheit
              </p>
            )}
          </Link>
        ))}
      </div>
    </AdminShell>
  );
}
